import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import get from 'lodash.get';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';

import { BuySharedHead } from './BuySharedHead/BuySharedHead';
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { BuySharedBreadcrumbs } from './BuySharedBreadcrumbs/BuySharedBreadcrumbs';
import { BuySharedTitle } from './BuySharedTitle/BuySharedTitle';
import { BuySharedResultContainer } from './BuySharedResultContainer/BuySharedResultContainer';
import { BuySharedPagination } from './BuySharedPagination/BuySharedPagination';
import { BuySharedDescription } from './BuySharedDescription/BuySharedDescription';
import { BuySharedNullSearchResults } from './BuySharedNullSearchResults/BuySharedNullSearchResults';
import { BuySharedFrequentlyAskedQuestions } from './BuySharedFrequentlyAskedQuestions/BuySharedFrequentlyAskedQuestions';

import styles from './BuySharedLayout.scss';
import { BuySharedMoreWaysToBrowse } from './BuySharedMoreWaysToBrowse/BuySharedMoreWaysToBrowse';

class BuySharedLayoutComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
    }

    componentDidMount() {
        // ServerVarsContext is not used here because we need initialDataLayerInfo only on client side
        const initialDataLayerInfo = serverVars.get('tracking.initialDataLayerInfo') || {};

        tracking.trackPageview({
            additional: {
                pageType: initialDataLayerInfo.pageType,
                pageSubType: initialDataLayerInfo.pageSubType,
                pageSection: get(this.props, 'viewer.buyPage.tracking.pageSection'),
            },
        });
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         */
        const fetchBuy = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        this.props.relay.refetch({ ...variables, fetchBuy }, renderVariables, callback);
    }

    render() {
        const { viewer, relay } = this.props;
        const { buyPage } = viewer;
        const { internalBuyLinks } = buyPage;

        const showNullSearchResultPage = !buyPage.totalResults;

        return (
            <Fragment>
                <BuySharedHead buyPage={buyPage} />
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />

                {showNullSearchResultPage ? (
                    <BuySharedNullSearchResults viewer={viewer} buyPage={buyPage} />
                ) : (
                    <Fragment>
                        <BuySharedBreadcrumbs buyPage={buyPage} />
                        <BuySharedTitle buyPage={buyPage} />
                        <div className={styles.container}>
                            <BuySharedResultContainer
                                viewer={viewer}
                                buyPage={buyPage}
                                relay={relay}
                            />
                            <BuySharedPagination buyPage={buyPage} />
                            <BuySharedDescription buyPage={buyPage} />
                        </div>

                        <BuySharedMoreWaysToBrowse
                            internalBuyLinks={internalBuyLinks}
                            viewer={viewer}
                        />

                        <BuySharedFrequentlyAskedQuestions buyPage={buyPage} />
                    </Fragment>
                )}
            </Fragment>
        );
    }
}

BuySharedLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
};

export const BuySharedLayout = createRefetchContainer(
    BuySharedLayoutComponent,
    {
        viewer: graphql`
            fragment BuySharedLayout_viewer on Viewer {
                buyPage(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    rootId: $rootId
                ) {
                    totalResults
                    tracking {
                        pageSection
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedPagination_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedNullSearchResults_buyPage
                    ...BuySharedFrequentlyAskedQuestions_buyPage
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                }
                ...BuySharedResultContainer_viewer
                ...BuySharedNullSearchResults_viewer
                ...BuySharedMoreWaysToBrowse_viewer
            }
        `,
    },
    graphql`
        query BuySharedLayoutRefetchQuery(
            $uriRef: String!
            $localeOrigin: String!
            $isTrade: Boolean!
            $showSeller: Boolean!
            $first: Int!
            $page: Int!
            $rootId: String!
            $fetchBuy: Boolean!
        ) {
            viewer {
                buyPage(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    rootId: $rootId
                ) @include(if: $fetchBuy) {
                    ...BuySharedResultContainer_buyPage
                }
            }
        }
    `
);
